import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { formatPhoneForDisplay } from "./utils/phone.utils"

// Export region utilities
export { buildRegionUrl, removeRegionFromPath, buildRegionAwareHref } from "./utils/region-url.utils"
export { formatPhoneForDisplay }

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

// Utility function to calculate distance between two points
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
}

interface LatLng {
    lat: number;
    lng: number;
}

// Spherical center calculation
export function calculateSphericalCenter(locations: LatLng[]): LatLng {
    // Convert lat/lng to radians
    const latRad = locations.map(loc => loc.lat * Math.PI / 180);
    const lngRad = locations.map(loc => loc.lng * Math.PI / 180);

    // Convert to Cartesian coordinates
    const x = latRad.map((lat, i) => Math.cos(lat) * Math.cos(lngRad[i]));
    const y = latRad.map((lat, i) => Math.cos(lat) * Math.sin(lngRad[i]));
    const z = latRad.map(lat => Math.sin(lat));

    // Average the Cartesian coordinates
    const xMean = x.reduce((a, b) => a + b, 0) / locations.length;
    const yMean = y.reduce((a, b) => a + b, 0) / locations.length;
    const zMean = z.reduce((a, b) => a + b, 0) / locations.length;

    // Convert back to lat/lng
    const centralLng = Math.atan2(yMean, xMean);
    const centralSquareRoot = Math.sqrt(xMean * xMean + yMean * yMean);
    const centralLat = Math.atan2(zMean, centralSquareRoot);

    return {
        lat: centralLat * 180 / Math.PI,
        lng: centralLng * 180 / Math.PI
    };
}

// Zoom level calculation without using Google Maps geometry library
export function calculateZoomLevel(locations: LatLng[]): number {
    // Find the max distance between points
    let maxDistance = 0;
    for (let i = 0; i < locations.length; i++) {
        for (let j = i + 1; j < locations.length; j++) {
            const distance = haversineDistance(
                locations[i].lat,
                locations[i].lng,
                locations[j].lat,
                locations[j].lng
            );
            maxDistance = Math.max(maxDistance, distance);
        }
    }

    // Rough zoom level calculation based on max distance
    const zoomLevels = [
        { maxDistance: 500, zoom: 16 },      // Very close (500m)
        { maxDistance: 1000, zoom: 15 },     // Close (1 km)
        { maxDistance: 2000, zoom: 14 },     // Neighborhood (2 km)
        { maxDistance: 5000, zoom: 13 },     // Small city area (5 km)
        { maxDistance: 10000, zoom: 12 },    // Medium city area (10 km)
        { maxDistance: 20000, zoom: 11 },    // Large city area (20 km)
        { maxDistance: 50000, zoom: 10 },    // Regional (50 km)
        { maxDistance: 100000, zoom: 9 },    // Multi-city (100 km)
        { maxDistance: 200000, zoom: 8 },    // State/Province level
        { zoom: 7 }                          // Country level fallback
    ];

    const zoomLevel = zoomLevels.find(level =>
        level.maxDistance === undefined || maxDistance <= level.maxDistance
    )?.zoom || 4;

    return zoomLevel;
}

/**
 * Function to shorten a name to initials,
 * e.g. "John Doe" -> "JD",
 * "John" -> "JO",
 */
export function shortenName(firstName?: string, lastName?: string) {
    if (!firstName) {
        return ""
    }
    if (!lastName) {
        const splittedFirstName = firstName.split(" ")
        if (splittedFirstName.length > 1) {
            const firstInitial = splittedFirstName[0].slice(0, 1).toUpperCase()
            const lastInitial = splittedFirstName[1].slice(0, 1).toUpperCase()
            return `${firstInitial}${lastInitial}`
        }
        return firstName.slice(0, 2).toUpperCase()
    }
    return `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase()
}

/**
 * Builds a URL with optional parameters
 * @param baseUrl - The base URL to build from (can be absolute or relative path)
 * @param params - An object containing key-value pairs of parameters to append to the URL
 * @returns The constructed URL with parameters
 * @example
 * buildUrl("https://example.com", {
 *     param1: "value1",
 *     param2: 2,
 *     param3: true
 * })
 * // returns "https://example.com?param1=value1&param2=2&param3=true"
 * 
 * buildUrl("/home-owners", {
 *     page: 1,
 *     limit: 10
 * })
 * // returns "/home-owners?page=1&limit=10"
 */
export function buildUrl<T extends Record<string, string | undefined | null | number | boolean | string[]>>(
    baseUrl: string, 
    params: T,
    options?: {
        // preserve the current params
        previousParams?: Partial<T>,
    }
) {
    // Check if the baseUrl is a relative path (doesn't have protocol)
    const allParams = { ...options?.previousParams, ...params }
    const isRelativePath = !baseUrl.includes('://');
    if (isRelativePath) {
        // Handle relative paths manually
        const queryParams = Object.entries(allParams)
            .filter(([_, value]) => value !== undefined && value !== null && value !== "")
            .flatMap(([key, value]) => {
                // Handle arrays by creating multiple key=value pairs
                if (Array.isArray(value)) {
                    return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v.toString())}`);
                }
                // Handle single values
                if (value !== undefined && value !== null && value !== "") {
                    return [`${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`];
                }
                return [];
            })
            .filter(param => param !== '')
            .join('&');

        return queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
    } else {
        // Handle absolute URLs with URL constructor
        try {
            const url = new URL(baseUrl);

            Object.entries(allParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    if (Array.isArray(value)) {
                        // For arrays, append each value separately
                        value.forEach(v => url.searchParams.append(key, v.toString()));
                    } else {
                        url.searchParams.append(key, value.toString());
                    }
                }
            });

            return url.toString();
        } catch (error) {
            // Fallback to manual handling if URL constructor fails
            const queryParams = Object.entries(allParams)
                .filter(([_, value]) => value !== undefined && value !== null)
                .flatMap(([key, value]) => {
                    // Handle arrays by creating multiple key=value pairs
                    if (Array.isArray(value)) {
                        return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v.toString())}`);
                    }
                    // Handle single values
                    if (value !== undefined && value !== null) {
                        return [`${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`];
                    }
                    return [];
                })
                .filter(param => param !== '')
                .join('&');

            return queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
        }
    }
}

/**
 * Formats a phone number for display.
 * Uses international format for all numbers (e.g. +44 7123 456789 for UK, +1 202 555 1234 for US).
 * @param phoneNumber - The phone number to format
 * @returns The formatted phone number for display
 */
export function formatPhoneNumber(phoneNumber?: string): string {
    if (!phoneNumber) {
        return ""
    }
    return formatPhoneForDisplay(phoneNumber);
}


export function removeUndefinedValues<T extends object>(obj: T) {
    return Object.fromEntries(
        Object.entries(obj).filter(([_, value]) => value !== undefined)
    );
}


/**
 * 
 * @param ms number to sleep
 * @returns 
 */
export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));



/**
 * Removes the country code from the phone number if it is there
 * @param value - The phone number to transform
 * @param code - The country code to remove, defaults to "+1"
 * @returns Phone number without the country code
 * @example
 * removeCountryCode("+11234567890") // returns "1234567890"
 * removeCountryCode("1234567890") // returns "1234567890"
 * removeCountryCode(undefined) // returns undefined
 * removeCountryCode(null) // returns undefined
 */
export function removeCountryCode(value: string, code?: string): string;
export function removeCountryCode(value: string | undefined | null, code?: string): string | undefined;
export function removeCountryCode(value: string | undefined | null, code: string = "+1") {
    if (!value) {
        return undefined
    }
    return value.startsWith(code) ? value.slice(code.length) : value
}


/**
 * Adds the country code to the phone number if it is not there
 * @param value - The phone number to transform
 * @param code - The country code to add, defaults to "+1"
 * @returns Phone number with the country code
 * @example
 * addCountryCode("1234567890") // returns "+11234567890"
 * addCountryCode("+11234567890") // returns "+11234567890"
 * addCountryCode(undefined) // returns undefined
 * addCountryCode(null) // returns undefined
 */
export function addCountryCode(value: string, code?: string): string;
export function addCountryCode(value: string | undefined | null, code?: string): string | undefined;
export function addCountryCode(value: string | undefined | null, code: string = "+1") {
    if (!value) {
        return undefined
    }
    return value.startsWith(code) ? value : `${code}${value}`
}

/**
 * Returns the values of the dirty fields from the values object
 * @param dirtyFieldsKeys - The keys of the dirty fields
 * @param values - The values of the dirty fields
 * @returns The values of the dirty fields
 * @example
 * getDirtyFieldsValues({ name: true, age: false }, { name: "John", age: 30 }) // returns { name: "John" }
 */
export function getDirtyFieldsValues<
    T extends object,
    V extends object
>(dirtyFieldsKeys: T, values: V) {
    return Object.keys(dirtyFieldsKeys || {}).reduce((acc, key) => {
        acc[key as keyof T] = values[key as keyof V]
        return acc;
    }, {} as Record<keyof T, any>)
}



/**
 * Converts an object to a FormData object
 */
export function objectToFormData(obj: Record<string, any>): FormData {
    const formData = new FormData();

    const appendFormData = (data: any, parentKey?: string) => {
        if (data === null || data === undefined) return;

        if (
            typeof data === 'object' &&
            !(data instanceof Date) &&
            !(data && typeof data === 'object' && 'name' in data && 'size' in data) &&
            !(data instanceof Blob)
        ) {
            if (Array.isArray(data)) {
                data.forEach((value) => {
                    const key = `${parentKey}`;
                    appendFormData(value, key);
                });
            } else {
                Object.entries(data).forEach(([key, value]) => {
                    const fullKey = parentKey ? `${parentKey}[${key}]` : key;
                    appendFormData(value, fullKey);
                });
            }
        } else {
            const value =
                data instanceof Date ? data.toISOString() : data;
            formData.append(parentKey!, value);
        }
    };

    appendFormData(obj);

    return formData;
}



/**
 * Converts a local date to a UTC date ignoring the timezone
 * @param date - The date to convert
 * @returns The UTC date
 * @example
 * localToUTCIgnoreTimezone(new Date()) // returns "2025-04-02T19:00:00.000Z"
 */
export function localToUTCIgnoreTimezone(date?: Date) {
    if (!date) {
        return undefined
    }
    return new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    ));
}